
import React, { useState } from 'react';
import { useTranslationUtils } from '@/hooks/useTranslationUtils';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { Badge } from '@/types/quests';
import BasicInfoSection from './form/BasicInfoSection';
import FormActions from './form/FormActions';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface BadgeFormProps {
  editingBadge: Badge | null;
  onCancel: () => void;
  onSave: () => void;
}

const BadgeForm: React.FC<BadgeFormProps> = ({
  editingBadge,
  onCancel,
  onSave
}) => {
  const { t } = useTranslationUtils();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formValues, setFormValues] = useState({
    name: editingBadge?.name || '',
    description: editingBadge?.description || '',
    image_url: editingBadge?.image_url || ''
  });
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
  };
  
  const handleImageUpload = (url: string) => {
    setFormValues(prev => ({ ...prev, image_url: url }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      
      if (!formValues.name) {
        toast({
          title: t('admin.error'),
          description: t('admin.badgeNameRequired', 'Badge name is required'),
          variant: 'destructive',
        });
        return;
      }
      
      // Create or update badge
      if (editingBadge) {
        const { error } = await supabase
          .from('badges')
          .update({
            name: formValues.name,
            description: formValues.description,
            image_url: formValues.image_url
          })
          .eq('id', editingBadge.id);
          
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('badges')
          .insert([{
            name: formValues.name,
            description: formValues.description,
            image_url: formValues.image_url
          }]);
          
        if (error) throw error;
      }
      
      onSave();
      
    } catch (error) {
      console.error('Error saving badge:', error);
      toast({
        title: t('admin.error'),
        description: t('admin.errorSavingBadge', 'Error saving badge'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="mb-8">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>
          {editingBadge 
            ? t('admin.editBadge', 'Edit Badge') 
            : t('admin.createBadge', 'Create Badge')}
        </CardTitle>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={onCancel}
        >
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <BasicInfoSection 
            formValues={formValues} 
            handleInputChange={handleInputChange}
            handleImageUpload={handleImageUpload}
          />
          
          <FormActions 
            editingBadge={editingBadge}
            onCancel={onCancel}
            isSubmitting={isSubmitting}
          />
        </form>
      </CardContent>
    </Card>
  );
};

export default BadgeForm;
